import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Avatar,
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
  IconButton,
  Grid
} from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';

const ClassInvitationModal = ({
  isOpen,
  onClose,
  onJoin,
  isEnrollLoading,
  enrollError,
  enrollClassInfo,
  isJoining
}) => {
  return (
    <Dialog
      open={isOpen}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onClose();
        }
      }}
      maxWidth="xs"
      fullWidth
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(8px)',
        },
        '& .MuiPaper-root': {
          borderRadius: '24px !important',
          overflow: 'hidden !important',
          position: 'relative',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15) !important',
          outline: 'none !important',
        }
      }}
    >
      {/* Top Banner / Profile Header */}
      {!isEnrollLoading && !enrollError && enrollClassInfo && (
        <Box
          sx={{
            background: 'linear-gradient(135deg, #FFF6F3 0%, #FFFDFD 100%)',
            borderBottom: '1px solid #FFEBE5',
            p: 3,
            pt: 4,
            position: 'relative'
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: '#94a3b8',
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(4px)',
              '&:hover': {
                color: '#64748b',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            <CloseIcon sx={{ fontSize: '18px' }} />
          </IconButton>

          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#FF682E', tracking: '1px', textTransform: 'uppercase', mb: 1.5, fontFamily: 'Poppins' }}>
            Invitation to Join
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={enrollClassInfo.teacherImage || '/images/avatar_real_1.png'}
              alt={enrollClassInfo.teacherName}
              sx={{
                width: 60,
                height: 60,
                border: '3px solid #ffffff',
                boxShadow: '0 8px 16px -4px rgba(255,104,46,0.25)',
              }}
            />
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '18.5px', color: '#0f172a', fontFamily: 'Poppins', lineHeight: 1.2 }}>
                {enrollClassInfo.teacherName}
              </Typography>
              <Box
                sx={{
                  display: 'inline-block',
                  mt: 0.75,
                  px: 1.5,
                  py: 0.25,
                  borderRadius: '50px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#FF682E',
                  bgcolor: '#FFF0EA',
                  fontFamily: 'Poppins',
                  border: '1px solid #FFE4D9'
                }}
              >
                {enrollClassInfo.institutionName || 'Individual Educator'}
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Loading and Error Headers */}
      {(isEnrollLoading || enrollError) && (
        <Box sx={{ p: 2, pb: 0, display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton onClick={onClose} sx={{ color: '#94a3b8' }}>
            <CloseIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Box>
      )}

      <DialogContent sx={{ p: 3, pt: enrollClassInfo && !isEnrollLoading && !enrollError ? 2.5 : 1 }}>
        {isEnrollLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2.5 }}>
            <CircularProgress size={44} sx={{ color: '#FF682E' }} />
            <Typography sx={{ fontFamily: 'Poppins', color: '#64748b', fontSize: '14.5px', fontWeight: 600 }}>
              Loading invitation details...
            </Typography>
          </Box>
        ) : enrollError ? (
          <Box sx={{ py: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </Box>
            <Typography sx={{ color: '#ef4444', fontWeight: 700, fontSize: '16px', fontFamily: 'Poppins' }}>
              {enrollError}
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '13px', fontFamily: 'Poppins', maxWidth: '280px', mx: 'auto' }}>
              The enrollment link may be invalid or has expired. Please verify and try again.
            </Typography>
          </Box>
        ) : enrollClassInfo ? (
          <Box>
            {/* Description */}
            {enrollClassInfo.description && (
              <Typography sx={{ fontSize: '13px', color: '#64748b', mb: 3, fontStyle: 'italic', fontFamily: 'Poppins', lineHeight: 1.5, borderLeft: '3px solid #FF682E', pl: 1.5 }}>
                "{enrollClassInfo.description}"
              </Typography>
            )}

            {/* Grid-based Specs (Modernized layout, full-width Class & Grade, half-width Subject & Fee) */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              {/* Class & Grade */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: 'rgba(139,92,246,0.08)', color: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <SchoolOutlinedIcon sx={{ fontSize: '20px' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '9.5px', color: '#94a3b8', fontWeight: 750, textTransform: 'uppercase', tracking: '0.5px', fontFamily: 'Poppins' }}>Class & Grade</Typography>
                    <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#334155', fontFamily: 'Poppins', lineHeight: 1.2 }}>
                      {enrollClassInfo.title} • {enrollClassInfo.gradeName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Subject */}
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: 'rgba(59,130,246,0.08)', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <AutoStoriesOutlinedIcon sx={{ fontSize: '20px' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '9.5px', color: '#94a3b8', fontWeight: 750, textTransform: 'uppercase', tracking: '0.5px', fontFamily: 'Poppins' }}>Subject</Typography>
                    <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: '#334155', fontFamily: 'Poppins', textTransform: 'capitalize', lineHeight: 1.2 }}>
                      {enrollClassInfo.subjectName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              {/* Enrollment Fee */}
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: 'rgba(16,185,129,0.08)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <AccountBalanceWalletOutlinedIcon sx={{ fontSize: '20px' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '9.5px', color: '#94a3b8', fontWeight: 750, textTransform: 'uppercase', tracking: '0.5px', fontFamily: 'Poppins' }}>Fee</Typography>
                    <Typography sx={{ fontSize: '13.5px', fontWeight: 800, color: '#10b981', fontFamily: 'Poppins', lineHeight: 1.2 }}>
                      {!enrollClassInfo.fee || enrollClassInfo.fee === 0 ? 'Free Class' : `LKR ${enrollClassInfo.fee.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mo`}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Schedule Section */}
            {enrollClassInfo.schedules && enrollClassInfo.schedules.length > 0 && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: '#F9FAFB',
                  borderRadius: '16px',
                  border: '1px solid #F1F5F9',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <AccessTimeOutlinedIcon sx={{ fontSize: '15px', color: '#FF682E' }} />
                  <Typography sx={{ fontSize: '10.5px', color: '#4B5563', fontWeight: 750, textTransform: 'uppercase', tracking: '0.5px', fontFamily: 'Poppins' }}>
                    Class Schedule
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  {enrollClassInfo.schedules.map((schedule, idx) => (
                    <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#475569', fontFamily: 'Poppins' }}>
                        {schedule.day}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748b', fontFamily: 'Poppins' }}>
                          {schedule.startTime} - {schedule.endTime}
                        </Typography>
                        <Box sx={{ fontSize: '10px', fontWeight: 600, px: 1, py: 0.25, borderRadius: '4px', bgcolor: 'rgba(255, 104, 46, 0.08)', color: '#FF682E', fontFamily: 'Poppins' }}>
                          {schedule.sessionType || 'Live'}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        ) : null}
      </DialogContent>

      {/* Action Buttons */}
      {!isEnrollLoading && !enrollError && enrollClassInfo && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 2 }}>
          <Button
            onClick={onClose}
            variant="text"
            sx={{
              borderRadius: '14px',
              height: '46px',
              textTransform: 'none',
              fontFamily: 'Poppins',
              fontSize: '14px',
              fontWeight: 700,
              color: '#64748b',
              flex: 1,
              '&:hover': {
                backgroundColor: '#f1f5f9',
                color: '#475569'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={onJoin}
            disabled={isJoining}
            variant="contained"
            sx={{
              borderRadius: '14px',
              height: '46px',
              textTransform: 'none',
              fontFamily: 'Poppins',
              fontSize: '14px',
              fontWeight: 700,
              backgroundColor: '#FF682E',
              color: '#ffffff',
              flex: 1.5,
              boxShadow: '0 8px 16px -4px rgba(255,104,46,0.3)',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: '#e05621',
                boxShadow: '0 12px 20px -4px rgba(255,104,46,0.4)',
                transform: 'translateY(-1.2px)'
              },
              '&:active': {
                transform: 'translateY(0)'
              }
            }}
          >
            Join Class
          </Button>
        </DialogActions>
      )}

      {/* Error Action Footer */}
      {enrollError && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={onClose}
            variant="contained"
            fullWidth
            sx={{
              borderRadius: '14px',
              height: '46px',
              textTransform: 'none',
              fontFamily: 'Poppins',
              fontSize: '14px',
              fontWeight: 700,
              backgroundColor: '#FF682E',
              color: '#ffffff',
              boxShadow: '0 8px 16px -4px rgba(255,104,46,0.3)',
              '&:hover': {
                backgroundColor: '#e05621'
              }
            }}
          >
            Okay
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ClassInvitationModal;
